<template>
    <div class="general-modal">
        <BaseModal padding="0px 0px 16px 0px"
                   :maxWidth="`${width}px`"
                   theme="notification-theme"
                   useClickOutsideClose
                   @close="handleClose">
            <template #header>
                <img :src="asset_cdn('assets/missions/notifications/notifications-arrow.png')"
                     class="header-arrow"
                     :width="arrowWidth"
                     :height="arrowHeight"
                     alt="arrow to notification"
                     title="arrow to notification">
                <img :src="asset_cdn('assets/missions/notifications/notifications.png')"
                     class="header-image"
                     :width="width"
                     :height="height"
                     alt="notification"
                     title="notification">
            </template>
            <template #content>
                <div class="general-modal__content">
                    <div class="general-modal__image">
                        🎁
                    </div>
                    <h2 class="general-modal__title">
                        <Translate translationKey="quests-dashboard.neverMissYourRewards"
                                   fallback="Never miss your rewards!"/>
                    </h2>
                    <div class="general-modal__subtitle">
                        <Translate translationKey="quests-dashboard.turnNotificationsOn"
                                   fallback="Turn notifications on to get updates about your rewards, airdrops and prizes." />
                    </div>
                </div>
            </template>
            <template #footer>
                <div class="general-modal__actions">
                    <span v-if="isSubscribeButton"
                          class="general-modal__action general-modal__action--confirm">
                        <BaseButton @click="handleClickSubscribe">
                            <Translate translationKey="quests-dashboard.subscribe"
                                       fallback="Subscribe" />
                        </BaseButton>
                    </span>
                    <span class="general-modal__action general-modal__action--confirm">
                        <BaseButton :variant="ButtonVariantEnum.QUATERNARY"
                                    class="button-close"
                                    @click="handleClose">
                            <Translate translationKey="quests-dashboard.later"
                                       fallback="Later" />
                        </BaseButton>
                    </span>
                </div>
            </template>
        </BaseModal>
    </div>
</template>

<script setup lang="ts">
    import { computed, watch } from 'vue';
    import BaseModal from '@/js/components/BaseModal.vue';
    import { ModalEnum, useModalStore } from '@/js/store/quests/game/modules/ModalStore';
    import { useModalActive } from '@/js/composables/useModalActive';
    import { asset_cdn } from '@/js/helpers/helpers';
    import { ButtonVariantEnum } from '@/js/types/components/ButtonVariantEnum';
    import BaseButton from '@/js/components/BaseButton.vue';
    import { useBrowserNotifications } from '@/js/quests/dashboard/composables/useBrowserNotifications';
    import { isFirefoxDesktop } from '@/js/auth/helpers/deviceUtils';
    import { useUserStore } from '@/js/store/quests/dashboard/UserStore';

    const modalStore = useModalStore();
    const userStore = useUserStore();

    const isBrowserFirefoxDesktop = computed(() => isFirefoxDesktop());
    const isNewUserSignup = computed(() => userStore.isNewUserSignup);
    const isSubscribeButton = computed(() => !isNewUserSignup.value && isBrowserFirefoxDesktop.value);

    const {
        isSubscribeAvailable,
        isInitialiseCompleted,
        isSubscribeCompleted,
        isPermissionGranted,
        activateSubscription,
        subscribeToNotifications,
        setRemindLater,
    } = useBrowserNotifications();

    const width = '520';
    const height = '91';

    const arrowWidth = '85';
    const arrowHeight = '191';

    const handleClose = () => {
        setRemindLater();

        modalStore.toggleModal(ModalEnum.BROWSER_NOTIFICATIONS, false);
    };

    const handleClickSubscribe = async () => {
        await subscribeToNotifications();
    };

    useModalActive();

    watch(() => isInitialiseCompleted.value, async (newValue) => {
        if (newValue) {
            if (!isSubscribeAvailable.value) {
                handleClose();
            } else if (!isSubscribeButton.value) {
                await subscribeToNotifications();
            }
        }
    }, { immediate: true });

    watch(() => isSubscribeCompleted.value, async (newValue) => {
        if (newValue) {
            if (isPermissionGranted.value) {
                activateSubscription();
            }

            handleClose();
        }
    });
</script>

<style scoped lang="scss">
@use "@/sass/design-system/variables" as variables;
@use "@/js/quests/dashboard/sass/mixins/_modal" as modalMixins;

.general-modal {
    @include modalMixins.modal;

    &__content {
        color: variables.$baseBlack;
    }

    &__image {
        margin-top: 24px;
        font-size: 80px;
        line-height: 1;
    }

    &__title {
        color: variables.$baseBlack;
    }

    &__subtitle {
        color: variables.$baseBlack;
        font-size: 18px;
        font-weight: 400;
    }

    &__actions {
        flex-direction: column;
        align-items: center;
        justify-content: center;
    }
}

.header-arrow {
    position: absolute;
    top: 0;
    left: 0;
    transform: translateY(-50%) translateX(-100%);
}

.header-image {
    width: 100%;
}

.button-close {
    color: variables.$gray500;

    &:active:enabled,
    &:hover:enabled {
        color: variables.$gray400;
    }
}
</style>
